import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Logo from "../components/Logo";
import { formatApiError } from "../lib/api";
import { ArrowRight, Lock, Star } from "@phosphor-icons/react";

export default function AdminLoginPage() {
  const { user, authChecked, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  if (authChecked && user && user.role === "admin") {
    return <Navigate to="/admin/dashboard" replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin/dashboard");
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen relative overflow-hidden flex flex-col"
      style={{ background: "#070b14" }}
      data-testid="admin-login-page"
    >
      {/* Rainbow swoosh backdrop */}
      <div
        className="absolute -top-40 left-1/2 -translate-x-1/2 w-[110vw] h-[560px] rainbow-swoosh pointer-events-none"
        aria-hidden
      />

      {/* Top brand bar */}
      <div className="relative px-6 lg:px-10 py-6 flex items-center justify-between">
        <Link to="/" data-testid="admin-login-brand">
          <Logo />
        </Link>
        <Link to="/" className="btn-link" data-testid="admin-back-home">
          ← Back to website
        </Link>
      </div>

      <div className="relative flex-1 flex items-center justify-center px-6 pb-16">
        <div className="w-full max-w-md text-center">
          <div className="flex justify-center mb-7">
            <Star size={24} weight="fill" className="text-[#dda632]" />
          </div>
          <div className="mx-auto mb-8 w-32 h-[5px] rounded-full bg-gradient-to-r from-[#55a2d2] via-[#af3fa6] to-[#dda632]" />

          <div className="eyebrow mb-5">Admin Portal</div>
          <h1 className="font-display text-5xl lg:text-6xl leading-[0.98] text-[#f5ecd9]">
            Welcome <span className="rainbow-text">back.</span>
          </h1>
          <p className="mt-6 text-lg text-[#a8b2cc] leading-relaxed">
            Sign in to manage slides, offers and enquiries.
          </p>

          <form
            onSubmit={onSubmit}
            className="mt-12 text-left space-y-5 bg-[#141e32]/85 backdrop-blur-sm rounded-[32px] border border-[#2c3854] p-8 lg:p-10 shadow-[0_30px_60px_-30px_rgba(11,17,32,0.18)]"
            data-testid="admin-login-form"
          >
            <div className="flex items-center gap-2 text-[#af3fa6]">
              <Lock size={16} weight="duotone" />
              <span className="eyebrow text-[#af3fa6]">Secure Access</span>
            </div>

            {err && (
              <div
                className="rounded-2xl border border-[#af3fa6] text-[#af3fa6] bg-[#e8f1f8] p-3 text-sm"
                data-testid="admin-login-error"
              >
                {err}
              </div>
            )}

            <div>
              <label className="block eyebrow mb-2">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-full bg-[#141e32] border border-[#2c3854] focus:border-[#f5ecd9] outline-none px-5 py-3 transition-colors"
                data-testid="admin-email-input"
                autoComplete="email"
              />
            </div>
            <div>
              <label className="block eyebrow mb-2">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-full bg-[#141e32] border border-[#2c3854] focus:border-[#f5ecd9] outline-none px-5 py-3 transition-colors"
                data-testid="admin-password-input"
                autoComplete="current-password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full justify-center disabled:opacity-60"
              data-testid="admin-submit-button"
            >
              {loading ? "Signing in…" : "Sign in"}
              <ArrowRight size={14} weight="bold" />
            </button>
          </form>
        </div>
      </div>

      <div className="rainbow-bar" />
    </div>
  );
}
